import type { Vowel } from '@archon3d/core';
import type { AudioBus, Vec3Like, Voice } from './audioBus';
import { envelope, hz, jitter, pick, rand } from './synth';
import type { VoiceProfile } from './voiceProfile';

/**
 * A throat in a handful of nodes: a sawtooth on a pitch contour (the vocal folds), pink noise
 * for breath, a fast amplitude flutter for growl, and three band-pass filters in parallel for
 * the vowel formants, which slide from one vowel to another over the utterance. Every utterance
 * is composed afresh – pitch, length, vowels and shakes are all drawn at random around the
 * profile – so a character never says exactly the same thing twice.
 */
export type UtteranceKind = 'hit' | 'attack' | 'death' | 'idle' | 'victory';

export interface UtteranceOptions {
  at?: Vec3Like;
  /** 0 … 1: how hard a hit was; a light one draws a grunt, a heavy one a scream. */
  intensity?: number;
  gain?: number;
  /** Seconds to wait before the utterance. */
  delay?: number;
}

/** Centre frequencies of the first three formants of a human throat, Hz. */
const FORMANTS: Record<Vowel, readonly [number, number, number]> = {
  a: [730, 1090, 2440],
  e: [530, 1840, 2480],
  i: [270, 2290, 3010],
  o: [570, 840, 2410],
  u: [300, 870, 2240],
};
const FORMANT_GAIN = [1, 0.55, 0.3] as const;
const FORMANT_Q = [7, 9, 11] as const;
/** Band-pass filters take most of the sawtooth's energy away; this puts it back. */
const MAKEUP = 1.8;

/** One burst of voice: a pitch contour through a vowel that may drift into another. */
interface Syllable {
  at: number;
  seconds: number;
  /** [seconds into the syllable, multiple of the base pitch]; the first point is at 0. */
  contour: [number, number][];
  attack: number;
  release: number;
  decay?: number;
  sustain?: number;
  gain: number;
  growl: number;
  breath: number;
  from: Vowel;
  to: Vowel;
  /** A shaking that grows through the syllable, 0 … 1. */
  tremble?: number;
}

export function utter(
  bus: AudioBus,
  profile: VoiceProfile,
  kind: UtteranceKind,
  options: UtteranceOptions = {},
): void {
  const v = bus.voice(`voice-${kind}`, {
    at: options.at,
    gain: options.gain ?? 0.6,
    reverb: 0.25,
    priority: kind === 'death' ? 'high' : 'normal',
  });
  if (!v) return;
  const t0 = v.now + (options.delay ?? 0);
  let end = t0;
  for (const s of compose(profile, kind, options.intensity ?? 0.5))
    end = Math.max(end, syllable(v, profile, s, t0 + s.at));
  v.end(end);
}

/** Writes the score for one utterance around the profile, with fresh randomness every time. */
function compose(p: VoiceProfile, kind: UtteranceKind, intensity: number): Syllable[] {
  const pace = 1 / p.tempo;
  const j = (value: number, amount = 0.1) => jitter(value, amount);
  const vowel = (wanted: Vowel[]): Vowel => {
    const both = p.vowels.filter((x) => wanted.includes(x));
    return pick(both.length > 0 ? both : p.vowels);
  };
  switch (kind) {
    case 'hit': {
      if (intensity < 0.45) {
        // A grunt: a short closed sound that drops away.
        const seconds = rand(0.1, 0.2) * pace;
        return [
          {
            at: 0,
            seconds,
            contour: [
              [0, j(1.35)],
              [0.04 * pace, j(1.1)],
              [seconds, j(0.78)],
            ],
            attack: 0.012,
            release: rand(0.06, 0.12),
            gain: 0.9,
            growl: 0.25,
            breath: 0.15,
            from: vowel(['u', 'o']),
            to: vowel(['a', 'o']),
          },
        ];
      }
      // A scream: leaps high, holds, and falls.
      const seconds = rand(0.3, 0.6) * pace * (0.8 + 0.4 * intensity);
      return [
        {
          at: 0,
          seconds,
          contour: [
            [0, j(1.6)],
            [0.06 * pace, j(2.3)],
            [seconds * 0.6, j(2)],
            [seconds, j(1.3, 0.15)],
          ],
          attack: 0.02,
          release: 0.15,
          decay: seconds * 0.5,
          sustain: 0.7,
          gain: 1,
          growl: 0.1,
          breath: 0.25,
          from: vowel(['a', 'e', 'i']),
          to: vowel(['a', 'o']),
        },
      ];
    }
    case 'attack': {
      // A shout: rises fast, holds, and settles.
      const seconds = rand(0.28, 0.55) * pace;
      return [
        {
          at: 0,
          seconds,
          contour: [
            [0, j(0.85)],
            [0.1 * pace, j(1.5)],
            [seconds * 0.7, j(1.45)],
            [seconds, j(1.1)],
          ],
          attack: 0.03,
          release: 0.12,
          gain: 1,
          growl: 0.35,
          breath: 0.1,
          from: vowel(['a', 'o']),
          to: vowel(['o', 'a']),
        },
      ];
    }
    case 'death': {
      // A long cry that sinks, shakes and runs out of breath.
      const seconds = rand(1.2, 2.2) * pace;
      return [
        {
          at: 0,
          seconds,
          contour: [
            [0, j(1.5)],
            [0.12 * pace, j(1.9)],
            [seconds * 0.45, j(1.2)],
            [seconds, j(0.45, 0.15)],
          ],
          attack: 0.04,
          release: 0.5,
          decay: seconds * 0.7,
          sustain: 0.4,
          gain: 1,
          growl: 0.15,
          breath: 0.5,
          from: vowel(['a', 'e']),
          to: vowel(['o', 'u']),
          tremble: 0.6,
        },
      ];
    }
    case 'victory': {
      // "Ha – HAA": a short shout, then a long one held high.
      const first = rand(0.1, 0.16) * pace;
      const second = rand(0.5, 0.8) * pace;
      return [
        {
          at: 0,
          seconds: first,
          contour: [
            [0, j(1.2)],
            [first, j(1.35)],
          ],
          attack: 0.02,
          release: 0.06,
          gain: 0.8,
          growl: 0.2,
          breath: 0.15,
          from: vowel(['a']),
          to: vowel(['a']),
        },
        {
          at: first + 0.08 * pace,
          seconds: second,
          contour: [
            [0, j(1.3)],
            [0.08 * pace, j(1.9)],
            [second * 0.8, j(1.85)],
            [second, j(1.5)],
          ],
          attack: 0.03,
          release: 0.25,
          decay: second * 0.6,
          sustain: 0.75,
          gain: 1,
          growl: 0.25,
          breath: 0.15,
          from: vowel(['a', 'e']),
          to: vowel(['a', 'o']),
        },
      ];
    }
    case 'idle': {
      // One to three soft syllables: a mutter, a chuckle, a hum.
      const count = 1 + Math.floor(Math.random() * 3);
      const out: Syllable[] = [];
      let at = 0;
      for (let i = 0; i < count; i++) {
        const seconds = rand(0.08, 0.16) * pace;
        out.push({
          at,
          seconds,
          contour: [
            [0, j(1.05, 0.08)],
            [seconds, j(0.9, 0.08)],
          ],
          attack: 0.015,
          release: 0.05,
          gain: 0.55,
          growl: 0.1,
          breath: 0.25,
          from: vowel(['e', 'o', 'u']),
          to: vowel(['e', 'o', 'u']),
        });
        at += seconds + rand(0.06, 0.12) * pace;
      }
      return out;
    }
  }
}

/** Builds the nodes for one syllable; returns the time it falls silent. */
function syllable(v: Voice, p: VoiceProfile, s: Syllable, t0: number): number {
  const ctx = v.context;
  const amp = ctx.createGain();
  const end = envelope(
    amp.gain,
    t0,
    s.seconds,
    { attack: s.attack, decay: s.decay, sustain: s.sustain, release: s.release },
    s.gain,
  );

  // The vocal folds: a sawtooth on the contour, wobbled by a vibrato that may grow into a tremble.
  const source = v.track(ctx.createOscillator());
  source.type = 'sawtooth';
  const [first] = s.contour;
  source.frequency.setValueAtTime(hz(ctx, p.pitch * (first?.[1] ?? 1)), t0);
  for (const [time, multiple] of s.contour.slice(1))
    source.frequency.exponentialRampToValueAtTime(hz(ctx, p.pitch * multiple), t0 + time);
  const vibrato = v.track(ctx.createOscillator());
  vibrato.frequency.value = rand(4.5, 7);
  const vibratoDepth = ctx.createGain();
  vibratoDepth.gain.setValueAtTime(p.vibrato, t0);
  if (s.tremble)
    vibratoDepth.gain.linearRampToValueAtTime(p.vibrato * (1 + 3 * s.tremble), t0 + s.seconds);
  vibrato.connect(vibratoDepth);
  vibratoDepth.connect(source.detune);

  // Breath: pink noise through the same throat, more of it as a dying voice gives out.
  const breath = v.track(ctx.createBufferSource());
  const buffer = v.noise('pink');
  breath.buffer = buffer;
  breath.loop = true;
  const breathGain = ctx.createGain();
  const breathLevel = 0.3 * Math.min(1, p.breathiness + s.breath);
  breathGain.gain.setValueAtTime(breathLevel, t0);
  if (s.tremble) breathGain.gain.linearRampToValueAtTime(breathLevel * 2.5, t0 + s.seconds);
  const mix = ctx.createGain();
  source.connect(mix);
  breath.connect(breathGain);
  breathGain.connect(mix);

  // Growl: a fast flutter of the amplitude, the way rough vocal folds slap.
  let voiced: AudioNode = mix;
  const growl = Math.min(1, p.growl + s.growl);
  if (growl > 0.05) {
    const rough = ctx.createGain();
    rough.gain.value = 1 - 0.55 * growl;
    const flutter = v.track(ctx.createOscillator());
    flutter.frequency.value = rand(26, 48);
    const flutterDepth = ctx.createGain();
    flutterDepth.gain.value = 0.55 * growl;
    flutter.connect(flutterDepth);
    flutterDepth.connect(rough.gain);
    mix.connect(rough);
    voiced = rough;
    flutter.start(t0);
    flutter.stop(end);
  }

  // The throat: three formant resonances in parallel, sliding from one vowel to the next.
  const throat = ctx.createGain();
  throat.gain.value = MAKEUP;
  const from = FORMANTS[s.from];
  const to = FORMANTS[s.to];
  for (let i = 0; i < 3; i++) {
    const formant = ctx.createBiquadFilter();
    formant.type = 'bandpass';
    formant.Q.value = FORMANT_Q[i]!;
    formant.frequency.setValueAtTime(hz(ctx, from[i]! * p.formantScale), t0);
    formant.frequency.linearRampToValueAtTime(hz(ctx, to[i]! * p.formantScale), t0 + s.seconds);
    const level = ctx.createGain();
    level.gain.value = FORMANT_GAIN[i]!;
    voiced.connect(formant);
    formant.connect(level);
    level.connect(throat);
  }

  // A tremble grows into a sob by the end of a death cry.
  let out: AudioNode = throat;
  if (s.tremble) {
    const shake = ctx.createGain();
    shake.gain.setValueAtTime(1, t0);
    shake.gain.linearRampToValueAtTime(1 - 0.5 * s.tremble, t0 + s.seconds);
    const lfo = v.track(ctx.createOscillator());
    lfo.frequency.value = rand(6, 9);
    const depth = ctx.createGain();
    depth.gain.setValueAtTime(0, t0);
    depth.gain.linearRampToValueAtTime(0.5 * s.tremble, t0 + s.seconds);
    lfo.connect(depth);
    depth.connect(shake.gain);
    throat.connect(shake);
    out = shake;
    lfo.start(t0);
    lfo.stop(end);
  }
  out.connect(amp);
  amp.connect(v.input);

  source.start(t0);
  source.stop(end);
  vibrato.start(t0);
  vibrato.stop(end);
  breath.start(t0, Math.random() * Math.max(0, buffer.duration - 0.5));
  breath.stop(end);
  return end;
}
